import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import LoadingModule from '../../LoadingModule/LoadingModule';
import OrganizationGroupRepository from '../../../data/repositories/OrganizationGroupRepository';
import LearnerRepository from '../../../data/repositories/LearnerRepository';
import {
  GroupMember,
  LearnerGroup,
  LearnerGroupPayload,
} from '../../../data/models/Groups/LearnerGroup';
import {LearnerListItem} from '../../../data/models/Learners/Learner';
import {getErrorFromCode} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {learnerGroupsText} from '../learnerGroupsText';

const WORKSPACE_PAGE_SIZE = 500;

class LearnerGroupsStore {
  private loadingModule = new LoadingModule();
  private groupsRepo = new OrganizationGroupRepository();
  private learnersRepo = new LearnerRepository();

  public groups: LearnerGroup[] = [];
  public learnerGroupNames: Record<string, string[]> = {};

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  private handleErrors(errors?: string[]) {
    if (errors && errors[0]) {
      toast.error(getErrorFromCode(errors[0]), toastConfig);
    }
  }

  public async fetchGroups(organizationId: string) {
    this.loadingModule.startLoading();
    const result = await this.groupsRepo.getGroups(organizationId);
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      this.groups = result.data;
    } else {
      this.handleErrors(result.errors);
    }
    return this.groups;
  }

  public async getGroupMembers(
    organizationId: string,
    groupId: string,
  ): Promise<GroupMember[]> {
    const result = await this.groupsRepo.getGroupMembers(
      organizationId,
      groupId,
    );
    if (!result.success) {
      this.handleErrors(result.errors);
      return [];
    }
    return result.data ?? [];
  }

  public async fetchLearnerGroupNames(organizationId: string) {
    const groups = await this.fetchGroups(organizationId);
    const members = await Promise.all(
      groups.map(group => this.getGroupMembers(organizationId, group.id)),
    );
    const names: Record<string, string[]> = {};
    groups.forEach((group, index) => {
      members[index].forEach(member => {
        names[member.learnerId] = [
          ...(names[member.learnerId] ?? []),
          group.name,
        ];
      });
    });
    this.learnerGroupNames = names;
  }

  public async getWorkspaceLearners(
    organizationId: string,
    page: number,
    pageSize: number,
    search?: string,
  ): Promise<{data: LearnerListItem[]; pagesCount: number}> {
    const result = await this.learnersRepo.getMyLearners(
      page,
      organizationId,
      pageSize,
      undefined,
      search,
    );
    if (!result.success) {
      this.handleErrors(result.errors);
      return {data: [], pagesCount: 0};
    }
    return {
      data: result.data?.data ?? [],
      pagesCount: result.data?.pagesCount ?? 0,
    };
  }

  public async getAllWorkspaceLearners(
    organizationId: string,
  ): Promise<LearnerListItem[]> {
    const first = await this.getWorkspaceLearners(
      organizationId,
      0,
      WORKSPACE_PAGE_SIZE,
    );
    const restPages = Array.from(
      {length: Math.max(first.pagesCount - 1, 0)},
      (_, index) =>
        this.getWorkspaceLearners(
          organizationId,
          index + 1,
          WORKSPACE_PAGE_SIZE,
        ),
    );
    const rest = await Promise.all(restPages);
    return [first, ...rest].flatMap(result => result.data);
  }

  public async createGroup(
    organizationId: string,
    payload: LearnerGroupPayload,
  ) {
    this.loadingModule.startLoading();
    const result = await this.groupsRepo.createGroup(organizationId, payload);
    this.loadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return undefined;
    }
    await this.fetchGroups(organizationId);
    toast.success(learnerGroupsText.groupCreated, toastConfig);
    return result.data?.groupId;
  }

  public async editGroup(
    organizationId: string,
    groupId: string,
    payload: LearnerGroupPayload,
  ) {
    this.loadingModule.startLoading();
    const result = await this.groupsRepo.editGroup(
      organizationId,
      groupId,
      payload,
    );
    this.loadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    await this.fetchGroups(organizationId);
    toast.success(learnerGroupsText.groupUpdated, toastConfig);
    return true;
  }

  public async deleteGroup(organizationId: string, groupId: string) {
    this.loadingModule.startLoading();
    const result = await this.groupsRepo.deleteGroup(organizationId, groupId);
    this.loadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    await this.fetchGroups(organizationId);
    toast.success(learnerGroupsText.groupDeleted, toastConfig);
    return true;
  }

  public async addLearners(
    organizationId: string,
    groupId: string,
    learnerIds: string[],
  ) {
    const result = await this.groupsRepo.addLearners(
      organizationId,
      groupId,
      learnerIds,
    );
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    toast.success(learnerGroupsText.membersAdded, toastConfig);
    return true;
  }

  public async removeLearners(
    organizationId: string,
    groupId: string,
    learnerIds: string[],
  ) {
    const result = await this.groupsRepo.removeLearners(
      organizationId,
      groupId,
      learnerIds,
    );
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    toast.success(learnerGroupsText.membersRemoved, toastConfig);
    return true;
  }

  public async enrollGroups(
    organizationId: string,
    groupIds: string[],
    publishedCourseId: string,
    dueDate?: Date,
  ) {
    this.loadingModule.startLoading();
    const errors: string[] = [];
    for (const groupId of groupIds) {
      const result = await this.groupsRepo.enrollGroup(
        organizationId,
        groupId,
        publishedCourseId,
        dueDate,
      );
      if (!result.success) {
        errors.push(...(result.errors ?? []));
      }
    }
    this.loadingModule.endLoading();
    if (errors.length) {
      this.handleErrors(errors);
      return false;
    }
    toast.success(
      learnerGroupsText.groupsEnrolled(groupIds.length),
      toastConfig,
    );
    return true;
  }
}

export default LearnerGroupsStore;
